import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useStructuraAuth } from './useStructuraAuth'
import { useTranslation } from '@structura/i18n'

// Landing page for the central auth service's verification e-mail
// ({app}/verifiser-epost?token=…). Consuming the token verifies the address
// and logs the user in — the central service returns a live session.
const VerifyEmail: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const authState = useStructuraAuth()
  const token: string = searchParams.get('token') ?? ''

  const [status, setStatus] = React.useState<'verifying' | 'success' | 'error'>('verifying')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const consumedRef = React.useRef<boolean>(false)

  React.useEffect(() => {
    if (token.length === 0 || consumedRef.current) {
      return
    }
    consumedRef.current = true
    authState.verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((error: unknown) => {
        setErrorMessage(error instanceof Error ? error.message : t('auth.verify.invalidLinkText'))
        setStatus('error')
      })
  }, [token, authState, t])

  const renderBody = (): React.ReactElement => {
    if (token.length === 0 || status === 'error') {
      return (
        <>
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
            style={{ width: '70px', height: '70px', background: 'rgba(178, 34, 34, 0.1)' }}
          >
            <i className="bi bi-x-circle" style={{ fontSize: '2rem', color: 'firebrick' }}></i>
          </div>
          <h2 className="fw-bold mb-3">{t('auth.verify.invalidLink')}</h2>
          <p className="text-muted mb-4">{errorMessage ?? t('auth.verify.invalidLinkText')}</p>
          <Link to="/register" className="btn btn-outline-secondary">
            <i className="bi bi-arrow-left me-2"></i>
            {t('auth.verify.goToApp')}
          </Link>
        </>
      )
    }

    if (status === 'verifying') {
      return (
        <>
          <span className="spinner-border mb-4" style={{ color: 'forestgreen' }}></span>
          <h2 className="fw-bold mb-3">{t('auth.verify.verifying')}</h2>
        </>
      )
    }

    return (
      <>
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
          style={{ width: '70px', height: '70px', background: 'rgba(34, 139, 34, 0.1)' }}
        >
          <i className="bi bi-check-circle" style={{ fontSize: '2rem', color: 'forestgreen' }}></i>
        </div>
        <h2 className="fw-bold mb-3">{t('auth.verify.success')}</h2>
        <p className="text-muted mb-4">{t('auth.verify.successText')}</p>
        <Link to="/" className="btn btn-lg" style={{ background: 'forestgreen', color: 'white' }}>
          <i className="bi bi-arrow-right-circle me-2"></i>
          {t('auth.verify.goToApp')}
        </Link>
      </>
    )
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
            <div className="card-body p-5 text-center">
              {renderBody()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmail
